import React, {
  FormEvent,
  useEffect,
  useRef,
  useState,
  useCallback
} from 'react';
import {
  Box,
  Flex,
  FormInput,
  IconButton,
  Text,
  styled
} from '@sparrowengg/twigs-react';
import {
  CloseIcon,
  DocumentClipIcon,
  ImageIcon
} from '@sparrowengg/twigs-react-icons';
import { FileUploadsProps } from '../types';
import { fileUploadTypes } from '../constants';
import FileUpload from 'commons/icons/file-upload';
import { truncateText } from 'commons/helper';

const FileUploads = ({
  getValues,
  setValue,
  isWebhookApp,
  zipFileName,
  hideZipUpload = false
}: FileUploadsProps) => {
  const iconRef = useRef<HTMLInputElement | null>(null);
  const zipRef = useRef<HTMLInputElement | null>(null);
  const prevIconRef = useRef<any>(null);
  const [previewImage, setPreviewImage] = useState<string | ArrayBuffer | null>(
    ''
  );
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState({
    iconName: '',
    zipFileName: zipFileName ?? ''
  });
  // Stable image src state - only updates when icon actually changes
  const [stableImageSrc, setStableImageSrc] = useState<string | null>(null);
  const [stableFileName, setStableFileName] = useState<string>('');
  const stableImageSrcRef = useRef<string | null>(null);
  const stableFileNameRef = useRef<string>('');
  const isIconLoadedRef = useRef(false);
  const { ZIP_FILE, ICON } = fileUploadTypes;

  const handleFileInput = (e: FormEvent, type: string) => {
    const inputValue = e.target as HTMLInputElement;
    const file: FileList | null = inputValue?.files;
    const filePath = inputValue?.value?.split('\\')?.pop()?.split('/').pop();
    if (type.includes(ICON)) {
      if (file && file?.length) {
        const selectedFile = file[0];
        const fileId = `${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`;

        // Update refs immediately to prevent reloads
        prevIconRef.current = fileId;
        stableFileNameRef.current = selectedFile.name;
        setStableFileName(selectedFile.name);
        isIconLoadedRef.current = true;

        let reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Update ref first (source of truth)
          stableImageSrcRef.current = result;
          // Update stable state (only updates when icon changes)
          setStableImageSrc(result);
          setPreviewImage(result);
          setFileName((prev) => ({ ...prev, iconName: selectedFile.name }));
          setIconUrl(null);
        };
        reader.readAsDataURL(selectedFile);
        setValue('icon', selectedFile, { shouldDirty: true });
      }
    } else {
      setFileName((prev) => ({ ...prev, zipFileName: filePath as string }));
      if (file && file?.length) {
        setValue('zipFile', file[0], { shouldDirty: true });
      }
    }
  };

  const StyledImage = styled('img', {
    width: 200,
    height: 200,
    borderRadius: '10px',
    objectFit: 'cover'
  });

  // Process icon changes with stable comparison
  const processIcon = useCallback(
    (iconValue: any, forceUpdate: boolean = false) => {
      const currentIconData = iconValue?.data || iconValue;

      // Create a stable identifier for comparison
      let currentIconId: string | null = null;
      if (currentIconData instanceof File) {
        currentIconId = `${currentIconData.name}-${currentIconData.size}-${currentIconData.lastModified}`;
      } else if (typeof currentIconData === 'string') {
        currentIconId = currentIconData;
      } else if (!currentIconData) {
        currentIconId = null;
      }

      // Only update if icon actually changed (unless forced, like on initial load)
      if (
        !forceUpdate &&
        currentIconId === prevIconRef.current &&
        isIconLoadedRef.current
      ) {
        return;
      }

      prevIconRef.current = currentIconId;

      if (currentIconData) {
        if (
          typeof currentIconData === 'string' &&
          (currentIconData.startsWith('http') ||
            currentIconData.startsWith('https'))
        ) {
          // Only update if URL actually changed
          if (stableImageSrcRef.current !== currentIconData) {
            const splitString = currentIconData.split('/');
            const extractedFileName = splitString[splitString.length - 1];
            stableFileNameRef.current = extractedFileName;
            stableImageSrcRef.current = currentIconData;
            setStableImageSrc(currentIconData);
            setStableFileName(extractedFileName);
            setFileName((prev) => ({
              ...prev,
              iconName: extractedFileName
            }));
            setIconUrl(currentIconData);
            setPreviewImage(null);
            isIconLoadedRef.current = true;
          }
        } else if (currentIconData instanceof File) {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            stableImageSrcRef.current = result;
            stableFileNameRef.current = currentIconData.name;
            setStableImageSrc(result);
            setStableFileName(currentIconData.name);
            setPreviewImage(result);
            setFileName((prev) => ({
              ...prev,
              iconName: currentIconData.name
            }));
            setIconUrl(null);
            isIconLoadedRef.current = true;
          };
          reader.readAsDataURL(currentIconData);
        }
      } else {
        stableImageSrcRef.current = null;
        stableFileNameRef.current = '';
        setStableImageSrc(null);
        setStableFileName('');
        setFileName((prev) => ({ ...prev, iconName: '' }));
        setPreviewImage(null);
        setIconUrl(null);
        isIconLoadedRef.current = false;
      }
    },
    []
  );

  // Initialize icon - check on mount and periodically until loaded
  // This handles the case where form is populated asynchronously
  useEffect(() => {
    const checkAndLoadIcon = () => {
      if (isIconLoadedRef.current) return;

      const currentIcon = getValues('icon');

      if (currentIcon) {
        // Force update on initial load to ensure icon is displayed
        if (isWebhookApp && currentIcon?.name) {
          // For webhook apps, icon might be a File object
          processIcon(currentIcon, true);
        } else if (currentIcon?.data) {
          // For regular apps, icon.data is the URL string
          processIcon(currentIcon, true);
        } else if (
          typeof currentIcon === 'string' &&
          (currentIcon.startsWith('http') || currentIcon.startsWith('https'))
        ) {
          // Handle case where icon is directly a URL string
          processIcon({ data: currentIcon }, true);
        }
      }
    };

    // Check immediately
    checkAndLoadIcon();

    // If not loaded, check again after a short delay (handles async form population)
    if (!isIconLoadedRef.current) {
      const timeoutId1 = setTimeout(() => {
        checkAndLoadIcon();
      }, 100);

      // Check one more time after a longer delay
      const timeoutId2 = setTimeout(() => {
        checkAndLoadIcon();
        // If still no icon after all checks, mark as loaded to stop checking
        if (!isIconLoadedRef.current) {
          isIconLoadedRef.current = true;
        }
      }, 500);

      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - icon updates are handled via handleFileInput

  return (
    <Flex flexDirection="column" gap="$20" css={{ marginRight: '$20' }}>
      {!isWebhookApp && !hideZipUpload ? (
        <>
          <Flex
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            onClick={() => zipRef?.current?.click()}
            css={{
              width: 200,
              height: 200,
              border: '2px dashed rgba(100, 116, 139, 0.15)',
              borderRadius: '10px',
              cursor: 'pointer'
            }}
          >
            <FormInput
              type="file"
              accept=".zip"
              ref={zipRef}
              onChange={(e: FormEvent) => handleFileInput(e, ZIP_FILE)}
              css={{ display: 'none' }}
            />
            {fileName?.zipFileName ? (
              <>
                <DocumentClipIcon size={48} color="#919191" />
                <Text
                  size="md"
                  weight="medium"
                  css={{ marginTop: '$8', color: '$neutral700' }}
                >
                  {truncateText(fileName.zipFileName, 20)}
                </Text>
              </>
            ) : (
              <>
                <FileUpload size={48} color="#919191" />
                <Text
                  size="md"
                  weight="medium"
                  css={{ marginTop: '$8', color: '$neutral700' }}
                >
                  Upload App Package
                </Text>
                <Text
                  size="xs"
                  css={{
                    color: '$neutral500'
                  }}
                >
                  Use .zip file of maximum 5mb
                </Text>
              </>
            )}
          </Flex>
        </>
      ) : null}
      <Flex
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        onClick={() => iconRef?.current?.click()}
        css={{
          width: 200,
          height: 200,
          border: '2px dashed rgba(100, 116, 139, 0.15)',
          borderRadius: '10px',
          cursor: 'pointer'
        }}
      >
        <input
          type="file"
          accept=".png, .jpg, .jpeg"
          ref={iconRef}
          onChange={(e: FormEvent) => handleFileInput(e, ICON)}
          style={{ display: 'none' }}
        />
        {/* Use stable state values - these only update when icon actually changes */}
        {stableImageSrc || previewImage || iconUrl ? (
          <Box
            css={{
              transition: 'all .3s ease',
              position: 'relative',
              '&:hover': {
                '#show-btn': {
                  opacity: 1
                },
                img: {
                  opacity: isWebhookApp ? 0.5 : 1
                }
              }
            }}
          >
            <StyledImage
              src={
                stableImageSrc ||
                (previewImage ? (previewImage as string) : '') ||
                iconUrl ||
                ''
              }
              alt={stableFileName || fileName.iconName}
              key={prevIconRef.current || 'icon'}
            />
            {isWebhookApp && (
              <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                id="show-btn"
                onClick={(e) => e.stopPropagation()}
                css={{
                  padding: '$16 $12',
                  opacity: 0,
                  position: 'absolute',
                  top: 2,
                  left: 1,
                  zIndex: 2,
                  background: 'rgba(255, 255, 255, 0.95)',
                  width: 198,
                  height: 198,
                  borderRadius: '10px',
                  transition: 'all .3s ease'
                }}
              >
                <Flex alignItems="center" gap="$2">
                  <IconButton
                    icon={<FileUpload />}
                    size="lg"
                    variant="ghost"
                    color="secondary"
                    onClick={() => {
                      iconRef?.current?.click();
                    }}
                  />
                  <IconButton
                    icon={<CloseIcon />}
                    size="lg"
                    variant="ghost"
                    color="secondary"
                    onClick={() => {
                      if (iconRef.current) {
                        iconRef.current.value = '';
                        stableImageSrcRef.current = null;
                        stableFileNameRef.current = '';
                        setStableImageSrc(null);
                        setStableFileName('');
                        setPreviewImage(null);
                        setIconUrl(null);
                        setFileName((prev) => ({ ...prev, iconName: '' }));
                        setValue('icon', '', { shouldDirty: true });
                        prevIconRef.current = null;
                        isIconLoadedRef.current = false;
                      }
                    }}
                  />
                </Flex>
                <Text
                  size="xs"
                  css={{
                    textAlign: 'center',
                    textWrap: 'pretty',
                    lineHeight: '$xs',
                    color: '$neutral500'
                  }}
                >
                  Use JPG, PNG upto 5MB max. Recommended size is 400 x 400px
                </Text>
              </Flex>
            )}
          </Box>
        ) : (
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            css={{ padding: '$16 $12' }}
          >
            <ImageIcon color="#6A6A6A" size={48} />
            <Text
              size="md"
              css={{ marginTop: '$8', color: '$neutral800' }}
              weight="medium"
            >
              Upload App Icon
            </Text>
            <Text
              size="xs"
              css={{
                textAlign: 'center',
                textWrap: 'pretty',
                lineHeight: '$xs',
                color: '$neutral500'
              }}
            >
              Use JPG, PNG upto 5MB max. Recommended size is 400 x 400px
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

// Memoize component to prevent re-renders when parent re-renders
export default React.memo(FileUploads);
